import BaseApiService from './BaseApiService.js';

class SubjectService extends BaseApiService {

  // ✅ Create a new subject
  async createSubject(subjectData) {
    // Validate required fields
    if (!subjectData.subjectName || !subjectData.subjectName.trim()) {
      throw new Error('Subject name is required');
    }
    if (!subjectData.subjectCode || !subjectData.subjectCode.trim()) {
      throw new Error('Subject code is required');
    }

    // Clean up payload - remove empty strings, convert to null if needed, remove orgId
    const cleanedData = {
      subjectName: subjectData.subjectName.trim(),
      subjectCode: subjectData.subjectCode.trim(),
      classLevel: subjectData.classLevel && subjectData.classLevel.trim() ? subjectData.classLevel.trim() : null,
      type: subjectData.type || 'Core',
      status: subjectData.status || 'ACTIVE',
      institutionType: subjectData.institutionType || 'SCHOOL',
      // Include college-specific fields if provided
      ...(subjectData.departmentName && { departmentName: subjectData.departmentName }),
      ...(subjectData.semester && { semester: subjectData.semester }),
      ...(subjectData.credits && { credits: subjectData.credits }),
      // Include optional fields if provided
      ...(subjectData.departmentId && { departmentId: subjectData.departmentId }),
      ...(subjectData.courseId && { courseId: subjectData.courseId }),
      ...(subjectData.courseName && { courseName: subjectData.courseName }),
      ...(subjectData.academicLevel && { academicLevel: subjectData.academicLevel }),
      ...(subjectData.subjectType && { subjectType: subjectData.subjectType })
      // Note: orgId is extracted from token by backend
    };

    console.log('📤 Creating subject with payload:', cleanedData);
    console.log('ℹ️ OrgId will be extracted from authentication token');
    
    try {
      const response = await this.makeRequest('/api/subjects/create', {
        method: 'POST',
        body: JSON.stringify(cleanedData),
      });
      console.log('✅ Subject created successfully:', response);
      return response;
    } catch (error) {
      console.error('❌ Failed to create subject:', error);
      throw error;
    }
  }

  // ✅ Get subjects by organization
  async getSubjectsByOrg(orgId) {
    if (!orgId) {
      console.warn('⚠️ OrgId is null or undefined, cannot fetch subjects');
      return [];
    }
    try {
      console.log(`🚀 Fetching all subjects and filtering by orgId: ${orgId} (Type: ${typeof orgId})`);
      // Fetch all subjects and filter by orgId on frontend
      const response = await this.makeRequest('/api/subjects/all', { 
        method: 'GET'
      });
      
      console.log('📥 Raw API response:', response);
      
      // Extract subjects array from response
      let subjectsArray = [];
      if (Array.isArray(response)) {
        subjectsArray = response;
        console.log('✅ Response is direct array with', subjectsArray.length, 'subjects');
      } else if (response && response.subjects && Array.isArray(response.subjects)) {
        subjectsArray = response.subjects;
        console.log('✅ Response has subjects property with', subjectsArray.length, 'subjects');
      } else if (response && Array.isArray(response.data)) {
        subjectsArray = response.data;
        console.log('✅ Response has data property with', subjectsArray.length, 'subjects');
      } else {
        console.warn('⚠️ Unexpected response format:', response);
        return [];
      }
      
      // Filter subjects by orgId (handle both numeric and string comparison)
      const numericOrgId = parseInt(orgId, 10);
      const filtered = subjectsArray.filter(subject => {
        if (!subject) return false;
        // Try numeric comparison first, then string comparison
        const subjectOrgId = subject.orgId;
        const matches = (subjectOrgId === numericOrgId) || 
                       (String(subjectOrgId) === String(orgId));
        if (matches) {
          console.log(`✅ Subject "${subject.subjectName}" matches orgId ${orgId}`);
        }
        return matches;
      });
      
      console.log(`📊 Filtered ${filtered.length} subjects for orgId: ${orgId}`);
      return filtered;
    } catch (error) {
      console.error(`❌ Failed to fetch subjects for org ${orgId}:`, error);
      return [];
    }
  }

  // ✅ Update subject
  async updateSubject(subjectId, subjectData) {
    return this.makeRequest(`/api/subjects/subject/${subjectId}`, {
      method: 'PUT',
      body: JSON.stringify(subjectData),
    });
  }

  // ✅ Delete subject
  async deleteSubject(subjectId) {
    return this.makeRequest(`/api/subjects/${subjectId}`, {
      method: 'DELETE',
    });
  }

  // ✅ Get subject allocations
  async getSubjectAllocations(subjectId, academicYear) {
    try {
      const url = `/api/subject-allocations/subject/${subjectId}` + (academicYear ? `?academicYear=${academicYear}` : '');
      return await this.makeRequest(url, { method: 'GET' });
    } catch (error) {
      console.error('Failed to fetch subject allocations:', error);
      return [];
    }
  }

  // ✅ Get subject allocations filtered
  async getSubjectAllocationsFiltered(academicYear, className, sectionName) {
    try {
      const params = new URLSearchParams();
      if (academicYear) params.append('academicYear', academicYear);
      if (className) params.append('className', className);
      if (sectionName) params.append('sectionName', sectionName);
      
      const url = `/api/subject-allocations?` + params.toString();
      return await this.makeRequest(url, { method: 'GET' });
    } catch (error) {
      console.error('Failed to fetch filtered subject allocations:', error);
      return [];
    }
  }

  // ✅ Get subject allocations by org
  async getOrgSubjectAllocations(orgId, academicYear) {
    try {
      const url = `/api/subject-allocations/org/${orgId}` + (academicYear ? `?academicYear=${academicYear}` : '');
      return await this.makeRequest(url, { method: 'GET' });
    } catch (error) {
      console.error('Failed to fetch org subject allocations:', error);
      return [];
    }
  }

  // ✅ Create subject allocation
  async createSubjectAllocation(allocationData) {
    return this.makeRequest('/api/subject-allocations/create', {
      method: 'POST',
      body: JSON.stringify(allocationData)
    });
  }

  // ✅ Delete subject allocation
  async deleteSubjectAllocation(allocationId) {
    return this.makeRequest(`/api/subject-allocations/${allocationId}`, {
      method: 'DELETE'
    });
  }

  // ✅ Bulk delete subject allocations by class and section
  async deleteClassSectionAllocations(className, sectionName, academicYear) {
    const params = new URLSearchParams();
    params.append('className', className);
    params.append('sectionName', sectionName);
    if (academicYear) params.append('academicYear', academicYear);
    
    return this.makeRequest(`/api/subject-allocations/class-section?` + params.toString(), {
      method: 'DELETE'
    });
  }
}

// ✅ Export single instance
const subjectService = new SubjectService();
export default subjectService;
